import { Button, CircularProgress, IconButton } from '@mui/material';
import React, { forwardRef, useRef, useEffect, useState } from 'react';
import useAssetronButtonStyles from './styles/assetron-button/useAssetronButtonStyles';
import AssetronButtonProps from './interface/AssetronButtonProps';

const AssetronButton = forwardRef<HTMLButtonElement, AssetronButtonProps>(
  (
    {
      size = 'md',
      hierarchy = 'primary',
      destructive = false,
      iconLeading,
      iconTrailing,
      disabled = false,
      isLoading = false,
      onClick,
      iconOnly = false,
      children,
      link,
      newTab = false,
      dataTestId,
      color,
      showNotAllowedCursor = false,
      ariaLabel,
      title,
    },
    ref,
  ) => {
    const { Icon, button, circularProgress } = useAssetronButtonStyles({
      size,
      hierarchy,
      destructive,
      color,
      showNotAllowedCursor,
    });

    const buttonRef = useRef<HTMLButtonElement>(null);
    const [buttonWidth, setButtonWidth] = useState<string | undefined>(
      undefined,
    );

    useEffect(() => {
      // Only lock width while loading to avoid layout shift.
      // Locking when not loading breaks responsiveness on viewport resize.
      if (!isLoading) {
        setButtonWidth(undefined);
        return;
      }

      if (buttonRef.current) {
        setButtonWidth(`${buttonRef.current.offsetWidth}px`);
      }
    }, [isLoading]);

    let targetValue;
    if (hierarchy === 'link' || hierarchy === 'link-color') {
      targetValue = newTab ? '_blank' : '_self';
    } else {
      targetValue = undefined;
    }

    const handleClick = (
      event: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    ) => {
      if (!isLoading && !disabled && onClick) {
        onClick(event);
      }
    };

    const showProgressBar =
      isLoading &&
      !disabled &&
      hierarchy !== 'link' &&
      hierarchy !== 'link-color';

    let minWidth: string | undefined;
    if (hierarchy === 'link' || hierarchy === 'link-color') {
      minWidth = 'auto';
    } else if (isLoading) {
      minWidth = buttonWidth;
    }

    const renderContent = () => {
      return showProgressBar ? (
        <CircularProgress sx={circularProgress} size={24} />
      ) : (
        <>
          {iconLeading}
          {children}
          {iconTrailing}
        </>
      );
    };

    if (
      iconOnly &&
      (hierarchy === 'primary' ||
        hierarchy === 'primary-color' ||
        hierarchy === 'secondary' ||
        hierarchy === 'secondary-color' ||
        hierarchy === 'tertiary')
    ) {
      return (
        <IconButton
          disableRipple
          sx={Icon}
          color={destructive ? 'error' : 'primary'}
          onClick={handleClick}
          data-testid={dataTestId}
          aria-label={ariaLabel}
          title={title}
          disabled={disabled}
          ref={ref}
        >
          {iconLeading}
        </IconButton>
      );
    }

    return (
      <Button
        target={targetValue}
        rel="noopener noreferrer"
        href={
          hierarchy === 'link' || hierarchy === 'link-color' ? link : undefined
        }
        data-testid={dataTestId}
        onClick={handleClick}
        disabled={disabled}
        aria-label={ariaLabel}
        title={title}
        sx={{
          ...button,
          minWidth,
        }}
        fullWidth
        ref={buttonRef}
        color={destructive ? 'error' : 'primary'}
      >
        {renderContent()}
      </Button>
    );
  },
);

export default AssetronButton;
